import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Eye, Ban, Trash2 } from "lucide-react";
import api, { openSuperAdminProof } from "../api";
import { useSuperAdmin } from "./SuperAdminLayout";
import { getTheme } from "../theme";
import { AdminModal, AdminTable, AreaTrendChart, BarTrendChart, ChartCard, ComboTrendChart, Kpi, LineTrendChart, PageHeader, StatusPill, TrendBars, UserPieChart, cardStyle, formatDate } from "./ui";

function useLoad(loader, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      setData(await loader());
    } catch (err) {
      setError(err.message || "Could not load Super Admin data.");
    } finally {
      setBusy(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return { data, error, busy, load, setData };
}

async function askReason(theme, title, confirmText) {
  const result = await Swal.fire({
    title,
    input: "textarea",
    inputLabel: "Reason",
    showCancelButton: true,
    confirmButtonText: confirmText,
    background: theme.canvas,
    color: theme.text,
  });
  if (!result.isConfirmed) return null;
  const reason = String(result.value || "").trim();
  if (reason.length < 4) {
    await Swal.fire({ icon: "error", title: "Reason required", text: "Enter at least 4 characters.", background: theme.canvas, color: theme.text });
    return null;
  }
  return reason;
}

function asList(payload, ...keys) {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
}

function normalizeUsers(payload, fallbackRole) {
  return asList(payload, "users").map((row) => {
    const role = row.role || fallbackRole || "user";
    return {
      ...row,
      key: `${role}-${row.id}`,
      role,
      name: row.name || row.company_name || "—",
      email: row.email || "—",
      phone: row.phone || "—",
      status: row.status || row.account_status || "active",
      verification: row.verification || (Number(row.is_verified) === 1 || (row.id_verification_method && String(row.id_verification_method).toLowerCase() !== "none") ? "verified" : "unverified"),
    };
  });
}

function ratingScore(row) {
  const keys = row?.performance != null
    ? ["performance", "character_score", "ai_knowledge", "teamwork", "reliability"]
    : ["salary", "environment", "location_score", "growth", "management"];
  const values = keys.map((key) => Number(row?.[key])).filter((value) => Number.isFinite(value));
  if (!values.length) return "—";
  return (values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1);
}

function normalizePipeline(payload, key) {
  return asList(payload, key, "rows", "records").map((row, index) => ({
    ...row,
    key: `${key}-${row.id || index}`,
    candidate: row.candidate || row.candidate_name || "—",
    job: row.job || row.role || row.job_role || "—",
    recruiter: row.recruiter || row.recruiter_name || "—",
    employer: row.employer || row.company_name || row.company || "—",
    status: row.status || "—",
    scheduled_at: row.scheduled_at || row.interview_date || null,
  }));
}

export function DashboardPage() {
  const ctx = useSuperAdmin() || {};
  const theme = ctx.theme || getTheme("super_admin", "dark");
  const { data, error, busy, load } = useLoad(() => api.get("/super-admin/dashboard"));
  const k = data?.kpis || {};
  const [openKey, setOpenKey] = useState(null);
  const [detail, setDetail] = useState({ rows: [], loading: false, error: "" });
  const requestSeq = useRef(0);

  const userSlices = [
    { key: "employees", label: "Employees", value: Number(k.employees) || 0, color: "#0284C7", role: "employee" },
    { key: "job_seekers", label: "Job Seekers", value: Number(k.job_seekers) || 0, color: "#38BDF8", role: "job_seeker" },
    { key: "employers", label: "Employers", value: Number(k.employers) || 0, color: "#F59E0B", role: "employer" },
    { key: "recruiters", label: "Recruiters", value: Number(k.recruiters) || 0, color: "#10B981", role: "recruiter" },
  ];

  async function openCard(key, fetcher) {
    if (openKey === key) {
      requestSeq.current += 1;
      setOpenKey(null);
      return;
    }
    const seq = ++requestSeq.current;
    setOpenKey(key);
    setDetail({ rows: [], loading: true, error: "" });
    try {
      const rows = await fetcher();
      if (seq !== requestSeq.current) return;
      setDetail({ rows: Array.isArray(rows) ? rows : [], loading: false, error: "" });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setDetail({ rows: [], loading: false, error: err.message || "Could not load list." });
    }
  }

  const fetchUsers = (role) => async () => normalizeUsers(await api.get(`/super-admin/users?filter_role=${encodeURIComponent(role)}`), role === "all" ? "" : role);

  const cards = [
    { key: "employees", label: "Employees", value: k.employees, fetch: fetchUsers("employee") },
    { key: "job_seekers", label: "Job Seekers", value: k.job_seekers, fetch: fetchUsers("job_seeker") },
    { key: "employers", label: "Employers", value: k.employers, fetch: fetchUsers("employer") },
    { key: "recruiters", label: "Recruiters", value: k.recruiters, fetch: fetchUsers("recruiter") },
    { key: "applications", label: "Applications", value: k.applications, fetch: async () => normalizePipeline(await api.get("/super-admin/applications"), "applications") },
    { key: "interviews", label: "Interviews", value: k.interviews, fetch: async () => normalizePipeline(await api.get("/super-admin/interviews"), "interviews") },
  ];

  const allUsersMeta = { key: "total_users", label: "Total Users", fetch: fetchUsers("all") };
  const openCardMeta = openKey === "total_users" ? allUsersMeta : cards.find((item) => item.key === openKey);
  const userListKeys = ["total_users", "employees", "job_seekers", "employers", "recruiters"];

  const closeModal = useCallback(() => {
    requestSeq.current += 1;
    setOpenKey(null);
  }, []);

  const listColumns = userListKeys.includes(openKey) ? [
    { key: "id", label: "ID" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone", render: (row) => row.phone || "—" },
    { key: "role", label: "Role", render: (row) => String(row.role || "").replace(/_/g, " ") || "—" },
    { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status || row.account_status || "active"} /> },
    { key: "verification", label: "Verification", render: (row) => <StatusPill theme={theme} value={row.verification} /> },
    { key: "created_at", label: "Created", render: (row) => formatDate(row.created_at) },
  ] : openKey === "applications" ? [
    { key: "candidate", label: "Candidate", render: (row) => row.candidate || row.candidate_name || "—" },
    { key: "job", label: "Job", render: (row) => row.job || row.role || "—" },
    { key: "recruiter", label: "Recruiter", render: (row) => row.recruiter || row.recruiter_name || "—" },
    { key: "employer", label: "Employer", render: (row) => row.employer || row.company_name || "—" },
    { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status} /> },
    { key: "created_at", label: "Applied", render: (row) => formatDate(row.created_at) },
  ] : [
    { key: "candidate", label: "Candidate", render: (row) => row.candidate || row.candidate_name || "—" },
    { key: "job", label: "Job", render: (row) => row.job || row.role || "—" },
    { key: "recruiter", label: "Recruiter", render: (row) => row.recruiter || row.recruiter_name || "—" },
    { key: "scheduled_at", label: "Date", render: (row) => formatDate(row.scheduled_at) || row.scheduled_at || row.interview_date || "—" },
    { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status} /> },
  ];

  return (
    <div className="d-flex flex-column h-100 justify-content-between" style={{ minHeight: 0, gap: 14 }}>
      <div className="flex-shrink-0">
        <PageHeader theme={theme} title="Dashboard" subtitle="Click a card or a chart to open its list." onRefresh={load} busy={busy} />
        {error ? <div className="alert alert-danger py-1 mb-2 small">{error}</div> : null}
      </div>

      {/* Enlarged First 6 KPI Cards */}
      <div className="row g-3 flex-shrink-0">
        {cards.map((card) => (
          <div className="col-6 col-md-4 col-xl-2" key={card.key}>
            <Kpi
              theme={theme}
              label={card.label}
              value={card.value}
              active={openKey === card.key}
              onClick={() => openCard(card.key, card.fetch)}
            />
          </div>
        ))}
      </div>

      {/* Middle 3 Trend Graphs */}
      <div className="row g-3 flex-grow-1" style={{ flex: "1 1 0", minHeight: 140 }}>
        <div className="col-12 col-lg-4 d-flex h-100">
          <ChartCard theme={theme} title="User registration" accent="#38BDF8">
            <LineTrendChart theme={theme} items={data?.trends?.users} color="#38BDF8" height={120} />
          </ChartCard>
        </div>
        <div className="col-12 col-lg-4 d-flex h-100">
          <ChartCard theme={theme} title="Applications" accent="#818CF8">
            <BarTrendChart theme={theme} items={data?.trends?.applications} color="#818CF8" height={120} />
          </ChartCard>
        </div>
        <div className="col-12 col-lg-4 d-flex h-100">
          <ChartCard theme={theme} title="Jobs posted" accent="#F59E0B">
            <AreaTrendChart theme={theme} items={data?.trends?.jobs} color="#F59E0B" height={120} />
          </ChartCard>
        </div>
      </div>

      {/* Bottom Row: Clear Pie Chart + Full-Width Combo Chart */}
      <div className="row g-3 flex-grow-1" style={{ flex: "1.15 1 0", minHeight: 160 }}>
        <div className="col-12 col-lg-5 d-flex h-100">
          <ChartCard theme={theme} title="Total Users Breakdown" accent="#0284C7" highlight={userListKeys.includes(openKey)}>
            <UserPieChart
              theme={theme}
              size={130}
              slices={userSlices}
              activeKey={openKey}
              onCenterClick={() => openCard("total_users", allUsersMeta.fetch)}
              onSliceClick={(slice) => {
                const card = cards.find((item) => item.key === slice.key);
                openCard(slice.key, card ? card.fetch : fetchUsers(slice.role || "all"));
              }}
            />
          </ChartCard>
        </div>
        <div className="col-12 col-lg-7 d-flex h-100">
          <ChartCard theme={theme} title="Registrations vs Applications" accent="#10B981">
            <ComboTrendChart
              theme={theme}
              bars={data?.trends?.applications}
              line={data?.trends?.users}
              barColor="#10B981"
              lineColor="#38BDF8"
              height={135}
            />
          </ChartCard>
        </div>
      </div>

      <AdminModal
        theme={theme}
        open={Boolean(openKey)}
        title={`${openCardMeta?.label || "Details"} list`}
        onClose={closeModal}
      >
        {detail.loading ? <div className="small" style={{ color: theme.textMute }}>Loading list…</div> : null}
        {detail.error ? <div className="alert alert-danger py-2">{detail.error}</div> : null}
        {!detail.loading ? <AdminTable theme={theme} rows={detail.rows} columns={listColumns} /> : null}
      </AdminModal>
    </div>
  );
}

export function UsersPage({ role = "all" }) {
  const { theme } = useSuperAdmin();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const title = role === "all" ? "All Users" : role === "job_seeker" ? "Job Seekers" : `${role.charAt(0).toUpperCase()}${role.slice(1)}s`;
  const { data, error, busy, load } = useLoad(() => {
    const params = new URLSearchParams({ filter_role: role });
    if (query) params.set("q", query);
    if (status) params.set("status", status);
    return api.get(`/super-admin/users?${params.toString()}`);
  }, [role, query, status]);

  async function setAccountStatus(row, nextStatus) {
    const reason = await askReason(theme, `${nextStatus} this account?`, nextStatus);
    if (!reason) return;
    await api.post(`/super-admin/users/${row.role || role}/${row.id}/status`, { status: nextStatus, reason });
    await load();
  }

  return (
    <div>
      <PageHeader theme={theme} title={title} subtitle="View, suspend, activate, or deactivate accounts. Permanent deletion is not available here." onRefresh={load} busy={busy}>
        <input className="form-control form-control-sm" style={{ maxWidth: 220 }} placeholder="Search name or email" value={query} onChange={(e) => setQuery(e.target.value)} />
        <select className="form-select form-select-sm" style={{ maxWidth: 160 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="deactivated">Deactivated</option>
        </select>
      </PageHeader>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <AdminTable
        theme={theme}
        rows={normalizeUsers(data, role === "all" ? "" : role)}
        columns={[
          { key: "id", label: "ID" },
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone", render: (row) => row.phone || "—" },
          { key: "role", label: "Role" },
          { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status || row.account_status} /> },
          { key: "verification", label: "Verification", render: (row) => <StatusPill theme={theme} value={row.verification} /> },
          { key: "created_at", label: "Created", render: (row) => formatDate(row.created_at) },
          { key: "last_login", label: "Last login", render: (row) => formatDate(row.last_login) },
          { key: "actions", label: "Actions", render: (row) => (
            <div className="d-flex flex-wrap gap-1">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => Swal.fire({ title: row.name || row.email, html: `<pre style="text-align:left;white-space:pre-wrap">${JSON.stringify(row, null, 2)}</pre>`, background: theme.canvas, color: theme.text })}>View</button>
              <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => setAccountStatus(row, "suspended")}>Suspend</button>
              <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setAccountStatus(row, "active")}>Activate</button>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setAccountStatus(row, "deactivated")}>Deactivate</button>
            </div>
          ) },
        ]}
      />
    </div>
  );
}

export function JobsPage({ view = "all" }) {
  const { theme } = useSuperAdmin();
  const endpoint = view === "applications" ? "/super-admin/applications" : view === "interviews" ? "/super-admin/interviews" : view === "syndication" ? "/super-admin/syndication" : "/super-admin/jobs";
  const { data, error, busy, load } = useLoad(() => api.get(endpoint), [endpoint]);

  async function setJobStatus(row, status) {
    const reason = await askReason(theme, `${status} job #${row.id}?`, status);
    if (!reason) return;
    await api.post(`/super-admin/jobs/${row.id}/status`, { status, reason });
    await load();
  }

  const title = view === "recruiter" ? "Recruiter Jobs" : view === "applications" ? "Applications" : view === "interviews" ? "Interviews" : view === "syndication" ? "Job Syndication" : "All Jobs";
  let columns = [];
  let rows = [];
  if (view === "applications") {
    rows = normalizePipeline(data, "applications");
    columns = [
      { key: "candidate", label: "Candidate", render: (row) => row.candidate || "—" },
      { key: "job", label: "Job", render: (row) => row.job || "—" },
      { key: "recruiter", label: "Recruiter", render: (row) => row.recruiter || "—" },
      { key: "employer", label: "Employer", render: (row) => row.employer || "—" },
      { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status} /> },
      { key: "created_at", label: "Applied", render: (row) => formatDate(row.created_at) },
    ];
  } else if (view === "interviews") {
    rows = normalizePipeline(data, "interviews");
    columns = [
      { key: "candidate", label: "Candidate", render: (row) => row.candidate || "—" },
      { key: "job", label: "Job", render: (row) => row.job || "—" },
      { key: "scheduled_at", label: "Interview date", render: (row) => formatDate(row.scheduled_at) || row.scheduled_at || "—" },
      { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status} /> },
      { key: "recruiter", label: "Recruiter", render: (row) => row.recruiter || "—" },
    ];
  } else if (view === "syndication") {
    rows = data?.posts || [];
    columns = [
      { key: "job", label: "Job" },
      { key: "platform", label: "Platform" },
      { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status} /> },
      { key: "updated_at", label: "Last sync", render: (row) => formatDate(row.updated_at || row.posted_at) },
      { key: "error_message", label: "Error", render: (row) => row.error_message || "—" },
    ];
  } else {
    rows = data?.jobs || [];
    columns = [
      { key: "id", label: "ID" },
      { key: "recruiter_name", label: "Recruiter" },
      { key: "role", label: "Job title" },
      { key: "applications", label: "Applications" },
      { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status} /> },
      { key: "syndication_status", label: "Syndication" },
      { key: "created_at", label: "Created", render: (row) => formatDate(row.created_at) },
      { key: "actions", label: "Actions", render: (row) => (
        <div className="d-flex flex-wrap gap-1">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => Swal.fire({ title: row.role, html: `<pre style="text-align:left;white-space:pre-wrap">${JSON.stringify(row, null, 2)}</pre>`, background: theme.canvas, color: theme.text })}>View</button>
          <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => setJobStatus(row, "suspended")}>Suspend</button>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setJobStatus(row, "closed")}>Close</button>
        </div>
      ) },
    ];
  }

  return (
    <div>
      <PageHeader theme={theme} title={title} subtitle="Recruiter jobs, applications, interviews, and board syndication." onRefresh={load} busy={busy} />
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <AdminTable theme={theme} rows={rows} columns={columns} />
    </div>
  );
}

export function RatingsPage({ type = "employee", moderation = false }) {
  const { theme } = useSuperAdmin();
  const [status, setStatus] = useState("");
  const ratingType = type === "employer" ? "employer" : "employee";
  const { data, error, busy, load } = useLoad(async () => {
    const qs = status ? `&status=${encodeURIComponent(status)}` : "";
    return api.get(`/super-admin/ratings?type=${ratingType}${qs}`);
  }, [ratingType, status]);

  async function moderate(row, action) {
    const reason = await askReason(theme, `${action} rating #${row.id}?`, action);
    if (!reason) return;
    await api.post(`/super-admin/ratings/${ratingType}/${row.id}/moderate`, { action, reason });
    await load();
  }

  async function invalidate(row) {
    const reason = await askReason(theme, "Mark document invalid?", "Mark invalid");
    if (!reason) return;
    await api.post(`/super-admin/ratings/${ratingType}/${row.id}/invalidate`, { reason });
    await load();
  }

  async function remove(row) {
    const reason = await askReason(theme, "Delete this rating and proof?", "Delete rating");
    if (!reason) return;
    await api.del(`/super-admin/ratings/${ratingType}/${row.id}?reason=${encodeURIComponent(reason)}`, { reason });
    await load();
  }

  return (
    <div>
      <PageHeader theme={theme} title={moderation ? "Rating Moderation" : (ratingType === "employer" ? "Employer Ratings" : "Employee Ratings")} subtitle="Keep moderation history. Hide or flag instead of destroying records wherever possible." onRefresh={load} busy={busy}>
        <select className="form-select form-select-sm" style={{ maxWidth: 180 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending">Under review</option>
          <option value="valid">Published</option>
          <option value="flagged">Flagged</option>
          <option value="hidden">Hidden</option>
          <option value="invalid">Invalid</option>
        </select>
      </PageHeader>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <AdminTable
        theme={theme}
        rows={data?.ratings || []}
        columns={[
          { key: "created_at", label: "When", render: (row) => formatDate(row.created_at) },
          { key: "employee_name", label: "Employee", render: (row) => <>{row.employee_name || row.employee_id}<div className="small" style={{ color: theme.textMute }}>{row.employee_email}</div></> },
          { key: "employer_name", label: "Employer", render: (row) => <>{row.employer_name || row.employer_id}<div className="small" style={{ color: theme.textMute }}>{row.employer_email}</div></> },
          { key: "document_status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.document_status} /> },
          { key: "actions", label: "Actions", render: (row) => (
            <div className="d-flex flex-wrap gap-1">
              {row.proof_file ? <button type="button" className="btn btn-sm btn-outline-info" onClick={() => openSuperAdminProof(ratingType, row.id).catch((err) => Swal.fire({ icon: "error", title: err.message }))}><Eye size={14} /></button> : null}
              <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => moderate(row, "flag")}>Flag</button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => moderate(row, "hide")}>Hide</button>
              <button type="button" className="btn btn-sm btn-outline-success" onClick={() => moderate(row, "restore")}>Restore</button>
              <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => invalidate(row)}><Ban size={14} /></button>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(row)}><Trash2 size={14} /></button>
            </div>
          ) },
        ]}
      />
    </div>
  );
}

export function VerificationPage({ view = "requests" }) {
  const { theme } = useSuperAdmin();
  const { data, error, busy, load } = useLoad(() => api.get(`/super-admin/verification?view=${view}`), [view]);
  const title = { requests: "Verification Requests", pending: "Pending Verification", verified: "Verified Profiles", rejected: "Rejected / Failed" }[view] || "Verification";

  async function setStatus(row, status) {
    const reason = await askReason(theme, `${status} request #${row.id}?`, status);
    if (!reason) return;
    await api.post(`/super-admin/verification/${row.id}/status`, { status, note: reason });
    await load();
  }

  return (
    <div>
      <PageHeader theme={theme} title={title} subtitle="Central verification console for requests, pending reviews, verified facts, and failures." onRefresh={load} busy={busy} />
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <AdminTable
        theme={theme}
        rows={data?.records || []}
        columns={view === "verified" ? [
          { key: "id", label: "ID" },
          { key: "subject_type", label: "User type" },
          { key: "subject_id", label: "User" },
          { key: "source", label: "Provider" },
          { key: "verified_field", label: "Type" },
          { key: "verified_at", label: "Verified", render: (row) => formatDate(row.verified_at) },
        ] : [
          { key: "id", label: "Request ID" },
          { key: "candidate_name", label: "User", render: (row) => row.candidate_name || row.matched_employee_name || "—" },
          { key: "id_type", label: "Type" },
          { key: "requested_by", label: "Requested by" },
          { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status} /> },
          { key: "created_at", label: "Created", render: (row) => formatDate(row.created_at) },
          { key: "actions", label: "Actions", render: (row) => view === "pending" ? (
            <div className="d-flex flex-wrap gap-1">
              <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setStatus(row, "completed")}>Approve</button>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setStatus(row, "rejected")}>Reject</button>
              <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => setStatus(row, "in_progress")}>Request info</button>
            </div>
          ) : <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => Swal.fire({ title: `Request #${row.id}`, html: `<pre style="text-align:left;white-space:pre-wrap">${JSON.stringify(row, null, 2)}</pre>`, background: theme.canvas, color: theme.text })}>View</button> },
        ]}
      />
    </div>
  );
}

export function CompliancePage({ view = "overview" }) {
  const { theme } = useSuperAdmin();
  const path = view === "consent" ? "/super-admin/consent-records" : view === "erasure" ? "/super-admin/erasure" : "/super-admin/compliance";
  const { data, error, busy, load } = useLoad(() => api.get(path), [path]);

  async function setErasure(row, status) {
    const reason = await askReason(theme, `Move erasure #${row.id} to ${status}?`, status);
    if (!reason) return;
    await api.post(`/super-admin/erasure/${row.id}/status`, { status, reason });
    await load();
  }

  if (view === "consent") {
    return (
      <div>
        <PageHeader theme={theme} title="Consent Records" subtitle="Granted and withdrawn DPDP consent." onRefresh={load} busy={busy} />
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <AdminTable theme={theme} rows={data?.records || []} columns={[
          { key: "subject_type", label: "User type" },
          { key: "subject_id", label: "User" },
          { key: "platform", label: "Consent type" },
          { key: "scope", label: "Scope" },
          { key: "consent_given_at", label: "Granted", render: (row) => formatDate(row.consent_given_at) },
          { key: "revoked_at", label: "Withdrawn", render: (row) => formatDate(row.revoked_at) },
          { key: "ip_address", label: "Source" },
        ]} />
      </div>
    );
  }
  if (view === "erasure") {
    return (
      <div>
        <PageHeader theme={theme} title="Erasure Requests" subtitle="Requested → Under review → Approved → Processing → Completed. Every change is recorded." onRefresh={load} busy={busy} />
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <AdminTable theme={theme} rows={data?.records || []} columns={[
          { key: "id", label: "ID" },
          { key: "subject_type", label: "Subject" },
          { key: "subject_id", label: "User" },
          { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status} /> },
          { key: "requested_at", label: "Requested", render: (row) => formatDate(row.requested_at) },
          { key: "reason", label: "Reason" },
          { key: "actions", label: "Workflow", render: (row) => (
            <div className="d-flex flex-wrap gap-1">
              {["under_review", "approved", "processing", "completed", "rejected"].map((status) => (
                <button key={status} type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setErasure(row, status)}>{status.replace("_", " ")}</button>
              ))}
            </div>
          ) },
        ]} />
      </div>
    );
  }
  return (
    <div>
      <PageHeader theme={theme} title="DPDP Overview" subtitle="Consent, erasure, verification, and compliance events." onRefresh={load} busy={busy} />
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <div className="row g-3">
        <div className="col-6 col-md-3"><Kpi theme={theme} label="Consent records" value={data?.consent_records} /></div>
        <div className="col-6 col-md-3"><Kpi theme={theme} label="Pending erasure" value={data?.pending_erasure} /></div>
        <div className="col-6 col-md-3"><Kpi theme={theme} label="Completed erasure" value={data?.completed_erasure} /></div>
        <div className="col-6 col-md-3"><Kpi theme={theme} label="Verification requests" value={data?.verification_requests} /></div>
        <div className="col-12 col-md-3"><Kpi theme={theme} label="Compliance events" value={data?.compliance_events} /></div>
      </div>
    </div>
  );
}

export function AnalyticsPage({ view = "platform" }) {
  const { theme } = useSuperAdmin();
  const { data, error, busy, load } = useLoad(() => api.get(`/super-admin/analytics?view=${view}`), [view]);
  const employee = data?.employee_trust || {};
  const employer = data?.employer_trust || {};
  return (
    <div>
      <PageHeader theme={theme} title={view === "employee-trust" ? "Employee Trust" : view === "employer-trust" ? "Employer Trust" : "Platform Analytics"} subtitle="Trust scores and growth trends from live WIBIL data." onRefresh={load} busy={busy} />
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {view !== "employer-trust" ? (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3"><Kpi theme={theme} label="Avg employee trust" value={employee.average_trust_score} /></div>
          <div className="col-6 col-md-3"><Kpi theme={theme} label="Employee ratings" value={employee.rating_count} /></div>
          <div className="col-6 col-md-3"><Kpi theme={theme} label="Verified employees" value={employee.verified} /></div>
          <div className="col-6 col-md-3"><Kpi theme={theme} label="Milestones" value={employee.milestones} /></div>
        </div>
      ) : null}
      {view !== "employee-trust" ? (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-4"><Kpi theme={theme} label="Avg employer score" value={employer.average_trust_score} /></div>
          <div className="col-6 col-md-4"><Kpi theme={theme} label="Employer ratings" value={employer.rating_count} /></div>
          <div className="col-6 col-md-4"><Kpi theme={theme} label="Verified employers" value={employer.verified} /></div>
        </div>
      ) : null}
      {view === "platform" ? (
        <div className="row g-3">
          {[["User growth", data?.trends?.users], ["Job growth", data?.trends?.jobs], ["Application growth", data?.trends?.applications], ["Verification growth", data?.trends?.verifications], ["Rating growth", data?.trends?.ratings]].map(([label, items]) => (
            <div className="col-12 col-md-6" key={label}>
              <div style={cardStyle(theme)}>
                <div className="small fw-bold mb-2" style={{ color: theme.textMute }}>{label}</div>
                <TrendBars theme={theme} items={items} />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function IntegrationsPage({ view = "health" }) {
  const { theme } = useSuperAdmin();
  const { data, error, busy, load } = useLoad(() => api.get("/super-admin/integrations"));
  const items = data?.integrations || [];
  const focus = {
    linkedin: "LinkedIn",
    indeed: "Indeed",
    google: "Google CSE",
    signcare: "SignCare",
    ai: "Gemini AI",
    health: null,
  }[view];
  const rows = focus ? items.filter((item) => item.name === focus) : items;
  return (
    <div>
      <PageHeader theme={theme} title={focus || "Integration Health"} subtitle="Connection status only. Secrets, tokens, and passwords are never shown." onRefresh={load} busy={busy} />
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <AdminTable theme={theme} rows={rows} columns={[
        { key: "name", label: "Integration" },
        { key: "status", label: "Status", render: (row) => <StatusPill theme={theme} value={row.status} /> },
        { key: "last_check", label: "Last check", render: (row) => formatDate(row.last_check) },
        { key: "hits", label: "Requests" },
        { key: "errors", label: "Errors" },
      ]} />
    </div>
  );
}

export function AuditPage({ view = "audit" }) {
  const { theme } = useSuperAdmin();
  const [query, setQuery] = useState("");
  const path = view === "audit" ? `/super-admin/audit${query ? `?q=${encodeURIComponent(query)}` : ""}` : `/super-admin/security?view=${view}`;
  const { data, error, busy, load } = useLoad(() => api.get(path), [path]);
  const title = { audit: "Audit Log", "admin-activity": "Admin Activity", logins: "Login Activity", events: "Security Events" }[view] || "Audit";
  return (
    <div>
      <PageHeader theme={theme} title={title} subtitle="Admin actions, logins, and security events." onRefresh={load} busy={busy}>
        {view === "audit" ? <input className="form-control form-control-sm" style={{ maxWidth: 220 }} placeholder="Search action or entity" value={query} onChange={(e) => setQuery(e.target.value)} /> : null}
      </PageHeader>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <AdminTable theme={theme} rows={data?.records || []} columns={[
        { key: "created_at", label: "When", render: (row) => formatDate(row.created_at) },
        { key: "actor", label: "Actor", render: (row) => row.actor_type ? `${row.actor_type} #${row.actor_id || row.admin_id || ""}` : `Admin #${row.admin_id || "—"}` },
        { key: "action", label: "Action", render: (row) => row.action || row.path },
        { key: "resource", label: "Entity", render: (row) => row.resource || row.path || "—" },
        { key: "ip_address", label: "IP" },
        { key: "reason", label: "Details", render: (row) => row.reason || row.status_code || "—" },
      ]} />
    </div>
  );
}

export function SystemPage({ view = "health" }) {
  const { theme } = useSuperAdmin();
  const { data, error, busy, load } = useLoad(() => api.get("/super-admin/system"));
  if (view === "environment") {
    const env = data?.environment || {};
    return (
      <div>
        <PageHeader theme={theme} title="Environment Status" subtitle="Safe runtime details only. Secrets are never exposed." onRefresh={load} busy={busy} />
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <div className="row g-3">
          {Object.entries(env).map(([key, value]) => (
            <div className="col-6 col-md-4" key={key}><Kpi theme={theme} label={key.replace(/_/g, " ")} value={String(value)} /></div>
          ))}
        </div>
      </div>
    );
  }
  if (view === "api") {
    return (
      <div>
        <PageHeader theme={theme} title="API Health" subtitle="Endpoint volume, latency, and errors." onRefresh={load} busy={busy} />
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <AdminTable theme={theme} rows={data?.endpoints || []} columns={[
          { key: "path", label: "Endpoint" },
          { key: "response_time", label: "Response time" },
          { key: "hits", label: "Hits" },
          { key: "errors", label: "Errors" },
        ]} />
      </div>
    );
  }
  if (view === "database") {
    return (
      <div>
        <PageHeader theme={theme} title="Database Health" subtitle="Connection and response time." onRefresh={load} busy={busy} />
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <div className="row g-3">
          <div className="col-6 col-md-4"><Kpi theme={theme} label="Connection" value={data?.database} /></div>
          <div className="col-6 col-md-4"><Kpi theme={theme} label="Response time" value={data?.database_ms != null ? `${data.database_ms} ms` : "—"} /></div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <PageHeader theme={theme} title="System Health" subtitle="API, database, frontend, storage, and mail." onRefresh={load} busy={busy} />
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <div className="row g-3">
        <div className="col-6 col-md-3"><Kpi theme={theme} label="API" value={data?.api} /></div>
        <div className="col-6 col-md-3"><Kpi theme={theme} label="Database" value={data?.database} /></div>
        <div className="col-6 col-md-3"><Kpi theme={theme} label="Frontend" value={data?.frontend} /></div>
        <div className="col-6 col-md-3"><Kpi theme={theme} label="Storage" value={data?.storage} /></div>
        <div className="col-6 col-md-3"><Kpi theme={theme} label="SMTP" value={data?.smtp} /></div>
        <div className="col-6 col-md-3"><Kpi theme={theme} label="API error rate" value={data?.api_error_rate != null ? `${data.api_error_rate}%` : "—"} /></div>
      </div>
    </div>
  );
}

export function ProfilePage({ security = false }) {
  const { theme, user } = useSuperAdmin();
  const { data, error, busy, load } = useLoad(() => api.get("/super-admin/profile"));
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const profile = data?.profile || {};

  async function changePassword(e) {
    e.preventDefault();
    try {
      await api.post("/super-admin/profile/password", { current_password: currentPassword, new_password: newPassword });
      setCurrentPassword("");
      setNewPassword("");
      await Swal.fire({ icon: "success", title: "Password updated", background: theme.canvas, color: theme.text });
    } catch (err) {
      await Swal.fire({ icon: "error", title: err.message || "Could not update password", background: theme.canvas, color: theme.text });
    }
  }

  if (security) {
    return (
      <div>
        <PageHeader theme={theme} title="Security" subtitle="Change password. MFA and session controls can be enabled in a later hardening sprint." onRefresh={load} busy={busy} />
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <div className="col-12 col-lg-6" style={cardStyle(theme)}>
          <form onSubmit={changePassword}>
            <label className="form-label small fw-bold">Current password</label>
            <input type="password" className="form-control mb-3" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            <label className="form-label small fw-bold">New password</label>
            <input type="password" className="form-control mb-3" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={6} required />
            <button type="submit" className="btn btn-primary">Change password</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader theme={theme} title="My Profile" subtitle="Super Admin account details." onRefresh={load} busy={busy} />
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <div className="row g-3">
        <div className="col-md-4"><Kpi theme={theme} label="Name" value={profile.name || user?.name} /></div>
        <div className="col-md-4"><Kpi theme={theme} label="Email" value={profile.email || user?.email} /></div>
        <div className="col-md-4"><Kpi theme={theme} label="Last login" value={formatDate(profile.last_login_at)} /></div>
      </div>
    </div>
  );
}

export function SuperAdminRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { if (location.pathname === "/super-admin/") navigate("/super-admin", { replace: true }); }, [location.pathname, navigate]);
  return null;
}
